import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { ChevronLeft, Image as ImageIcon, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, useStripe, useElements } from '@stripe/react-stripe-js';
import StepIndicator from './StepIndicator';
import StripeCardInput from './StripeCardInput';
import { updateUserProfile } from '../../services/authService';

const isDevMode = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const stripePromise = isDevMode ? Promise.resolve(null) : loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const PaymentPageStyles = () => (
  <style>{`
    .payment-page {
      max-width: 1200px;
      margin: 0 auto;
      padding: 0 clamp(10px, 2vw, 20px);
    }

    .payment-container {
      display: grid;
      grid-template-columns: 1.5fr 1fr;
      gap: clamp(20px, 3vw, 30px);
    }

    .payment-main {
      background: #fff;
      border-radius: 4px;
      padding: clamp(20px, 4vw, 40px);
      box-shadow: 0 4px 20px rgba(0,0,0,0.06);
    }

    .payment-title {
      font-size: clamp(1rem, 3vw, 1.1rem);
      font-weight: 400;
      color: #1a1a1a;
      margin-bottom: clamp(15px, 3vw, 25px);
      padding-bottom: 15px;
      border-bottom: 1px solid #f1f5f9;
    }

    /* 分隔線樣式 */
    .section-divider {
      height: 1px;
      background-color: #e8e8e8;
      margin: 40px 0;
      border: none;
    }

    .payment-form {
      display: flex;
      flex-direction: column;
      gap: clamp(15px, 3vw, 20px);
    }

    .form-group {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .form-label {
      font-size: clamp(0.75rem, 1.8vw, 0.8rem);
      font-weight: 400;
      color: #333;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .form-input, .form-textarea {
      padding: clamp(12px, 2.5vw, 14px) 16px;
      border: 1px solid #e8e8e8;
      border-radius: 4px;
      font-size: 16px;
      font-family: inherit;
      transition: all 0.3s ease;
      background: #fff;
      width: 100%;
      box-sizing: border-box;
    }

    .form-textarea {
      min-height: 100px;
      resize: vertical;
      line-height: 1.5;
    }

    .readonly-address {
      font-size: 16px;
      color: #666;
      padding: clamp(12px, 2.5vw, 14px) 16px;
      background: #f8f8f8;
      border-radius: 4px;
      border: 1px solid #e8e8e8;
      box-sizing: border-box;
      width: 100%;
    }

    .form-input:focus, .form-textarea:focus {
      outline: none;
      border-color: #2F7D5E;
      box-shadow: 0 0 0 3px rgba(47, 125, 94, 0.15);
    }

    .form-input:disabled, .form-textarea:disabled {
      background-color: #f5f5f5;
      cursor: not-allowed;
    }
    .form-input:-webkit-autofill,
    .form-input:-webkit-autofill:focus {
      -webkit-box-shadow: 0 0 0 1000px #fff inset;
      box-shadow: 0 0 0 1000px #fff inset;
      -webkit-text-fill-color: inherit;
    }

    .form-row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: clamp(15px, 3vw, 20px);
    }

    /* ... (保留原本的其他 CSS) ... */
    .payment-actions { display: flex; gap: 15px; margin-top: clamp(15px, 3vw, 20px); flex-wrap: wrap; }
    .payment-summary { background: #fff; border: 1px solid #e2e8f0; border-radius: 4px; height: fit-content; box-shadow: 0 4px 20px rgba(0,0,0,0.04); overflow: hidden; box-sizing: border-box; }
    .payment-summary-title { font-size: 1.1rem; font-weight: 400; color: #2F7D5E; padding: 12px 16px; border-bottom: 1px solid #f1f5f9; display: flex; align-items: center; gap: 10px; margin: 0; }
    .payment-items { max-height: 400px; overflow-y: auto; }
    .payment-item { padding: 12px 16px; border-bottom: 1px solid #f1f5f9; }
    .payment-item:last-child { border-bottom: none; }
    .payment-item-header { margin-bottom: 8px; padding-bottom: 4px; border-bottom: 1px solid #f1f5f9; }
    .payment-date { font-size: 0.85rem; font-weight: 400; color: #1a1a1a; }
    .payment-weekday { display: none; }
    .payment-meal-row { display: grid; grid-template-columns: 48px 1fr; gap: 10px; margin-bottom: 16px; align-items: start; }
    .payment-meal-row:last-child { margin-bottom: 0; }
    .payment-meal-thumbnail { width: 48px; height: 48px; border-radius: 4px; object-fit: cover; background: #f1f5f9; border: 1px solid #e2e8f0; }
    .thumbnail-placeholder { display: flex; align-items: center; justify-content: center; color: #ccc; }
    .payment-meal-info-block { display: flex; flex-direction: column; width: 100%; min-width: 0; }
    .meal-tag { font-size: 0.9rem; font-weight: 400; padding: 0; margin-bottom: 2px; display: block; }
    .tag-lunch { color: #c2410c; }
    .tag-dinner { color: #1e40af; }
    .dish-title-row { display: flex; justify-content: space-between; align-items: flex-start; gap: 8px; margin-bottom: 2px; }
    .payment-meal-name { font-size: 0.9rem; font-weight: 400; color: #1e293b; line-height: 1.35; }
    .payment-meal-price { font-size: 0.9rem; font-weight: 400; color: #1e293b; white-space: nowrap; text-align: right; }
    .mod-breakdown { display: flex; flex-direction: column; gap: 2px; width: 100%; }
    .mod-row { display: flex; justify-content: space-between; align-items: flex-start; font-size: 0.8rem; color: #64748b; line-height: 1.4; }
    .mod-name { text-align: left; flex: 1; padding-right: 12px; }
    .mod-price { font-weight: 400; color: #64748b; white-space: nowrap; }
    .payment-total-section { border-top: 1px solid #e2e8f0; padding: 12px 16px; }
    .payment-bill-row { display: flex; justify-content: space-between; margin-bottom: 8px; font-size: 0.9rem; font-weight: 400; color: #334155; }
    .payment-bill-row.fee-row { margin-bottom: 12px; padding-bottom: 12px; border-bottom: 1px dashed #e2e8f0; }
    .payment-tax { color: #94a3b8; font-size: 0.85rem; }
    .payment-total { display: flex; justify-content: space-between; font-size: 1.2rem; font-weight: 400; color: #1a1a1a; margin-top: 12px; padding-top: 12px; border-top: 1px solid #ccc; }
    .payment-btn { padding: clamp(14px, 3vw, 16px) clamp(30px, 5vw, 40px); border: none; font-size: 16px; font-weight: 400; cursor: pointer; transition: all 0.3s ease; display: flex; align-items: center; justify-content: center; gap: 10px; letter-spacing: 0.3px; border-radius: 4px; flex: 1; min-width: 140px; }
    .payment-btn:active { transform: scale(0.98); }
    .payment-btn-primary { background: #2F7D5E; color: #fff; box-shadow: 0 4px 12px rgba(47,125,94,0.2); border: 1px solid #2F7D5E; }
    .payment-btn-secondary { background: #fff; color: #2F7D5E; border: 1px solid #e0e0e0; }

    @media (max-width: 1024px) {
      .payment-container { grid-template-columns: 1fr; }
      .payment-summary { position: static; order: -1; width: 100%; }
      .payment-main { order: 1; }
    }
    @media (max-width: 640px) {
      .payment-page { padding: 0; }
      .form-row { grid-template-columns: 1fr; }
      .payment-actions { flex-direction: column; }
      .payment-btn { width: 100%; }
      .payment-items { max-height: 300px; }
    }
    @media (min-width: 1025px) {
      .payment-summary { position: sticky; top: 90px; }
      .payment-btn:hover { transform: translateY(-2px); }
    }

    .spinner { animation: spin 1s linear infinite; margin-right: 8px; }
    @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
  `}</style>
);

const PaymentPageInner = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('booking');

  const {
    next7Days,
    selectedDays,
    mealSelections,
    menuOptions,
    modifierGroupsList,
    handlePayment,
    bookingMode,
    matchedZone,
    orderType,
    deliveryAddress,
    unitNumber,
    city,
    postalCode,
    province,
    deliveryNotes, setDeliveryNotes,
    user, // 🔥 從 Context 獲取登入者資訊
    packagingFee,
  } = useOutletContext();

  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [stripeError, setStripeError] = useState(null);
  const [paymentSucceeded, setPaymentSucceeded] = useState(false); // prevent double-charge

  // Contact info: prefer user profile from DB (cross-device), fallback to localStorage
  let savedContact = null;
  try { savedContact = JSON.parse(localStorage.getItem('saved_contact') || 'null'); } catch { /* ignore */ }
  const dbName = user?.name || user?.user_metadata?.name || '';
  const dbPhone = user?.phone_number || user?.user_metadata?.phone || '';
  const [saveContact, setSaveContact] = useState(!!savedContact || !!(dbName && dbPhone));
  const [formData, setFormData] = useState({
    // 聯繫與配送資訊
    contactName: savedContact?.name || dbName || '',
    contactPhone: savedContact?.phone || dbPhone || '',

    // 付款資訊
    cardHolder: '',
  });

  useEffect(() => { window.scrollTo(0, 0); }, []);

  // 自動填入使用者資料 (localStorage > user metadata)
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        contactName: prev.contactName || user.user_metadata?.name || '',
        contactPhone: prev.contactPhone || user.user_metadata?.phone || '',
      }));
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const toggleSaveContact = () => {
    const next = !saveContact;
    setSaveContact(next);
    if (!next) localStorage.removeItem('saved_contact');
  };

  // --- 計算邏輯 (保持不變) ---
  const orderBreakdown = useMemo(() => {
    let subtotal = 0;
    let gst = 0;
    let pst = 0;

    selectedDays.forEach(dayId => {
      const daySelections = mealSelections[dayId] || {};
      ['lunch', 'dinner'].forEach(type => {
        const selection = daySelections[type];
        if (selection) {
          const mealId = typeof selection === 'object' ? selection.id : selection;
          const meal = menuOptions.find(m => m.id === mealId);
          if (meal) {
            let itemPrice = meal.price || 0;
            subtotal += itemPrice;
            if (meal.tax_gst) gst += itemPrice * 0.05;
            if (meal.tax_pst) pst += itemPrice * 0.07;
            if (selection.modifiers && Array.isArray(selection.modifiers)) {
              selection.modifiers.forEach(mod => {
                const modPrice = mod.price || 0;
                subtotal += modPrice;
                if (meal.tax_gst) gst += modPrice * 0.05;
                if (meal.tax_pst) pst += modPrice * 0.07;
              });
            }
          }
        }
      });
    });

    // 計算總餐數：3餐以上免運費
    let totalMealCount = 0;
    selectedDays.forEach(dayId => {
      const s = mealSelections[dayId] || {};
      if (s.lunch) totalMealCount++;
      if (s.dinner) totalMealCount++;
    });

    let deliveryFee = 0;
    if (matchedZone && totalMealCount < 3) {
      if (matchedZone.delivery_fee !== undefined && matchedZone.delivery_fee !== null) {
        deliveryFee = Number(matchedZone.delivery_fee);
      } else if (matchedZone.deliveryFee !== undefined && matchedZone.deliveryFee !== null) {
        deliveryFee = Number(matchedZone.deliveryFee);
      }
    }
    const freeDelivery = totalMealCount >= 3;
    const pkgFee = packagingFee || 0;

    return {
      subtotal, gst, pst, packagingFee: pkgFee, deliveryFee, freeDelivery, totalMealCount,
      grandTotal: subtotal + gst + pst + pkgFee + deliveryFee
    };
  }, [selectedDays, mealSelections, menuOptions, matchedZone, packagingFee]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStripeError(null);

    // 驗證
    if (!formData.contactName || !formData.contactPhone) {
      alert(t('payment.alert_contact'));
      return;
    }
    // Save contact info: store in DB (cross-device) + localStorage (fallback)
    if (saveContact) {
      localStorage.setItem('saved_contact', JSON.stringify({ name: formData.contactName, phone: formData.contactPhone }));
      if (user?.id) {
        updateUserProfile(user.id, { name: formData.contactName, phone_number: formData.contactPhone }).catch(() => {});
      }
    }
    if (!isDevMode && !formData.cardHolder) {
      alert(t('payment.alert_payment'));
      return;
    }
    if (!isDevMode && (!stripe || !elements)) {
      return;
    }

    setIsProcessing(true);

    try {
      if (isDevMode) {
        // Dev mode: skip Stripe entirely, create order directly
        await handlePayment(formData, orderBreakdown, 'dev_test_' + Date.now());
      } else {
        // 1. Create PaymentIntent with server-side price verification
        const mealsForServer = [];
        selectedDays.forEach(dayId => {
          const daySelections = mealSelections[dayId] || {};
          ['lunch', 'dinner'].forEach(type => {
            const selection = daySelections[type];
            if (selection) {
              const mealId = typeof selection === 'object' ? selection.id : selection;
              const modifierIds = (selection.modifiers && Array.isArray(selection.modifiers))
                ? selection.modifiers.map(m => m.id)
                : [];
              mealsForServer.push({ menu_item_id: mealId, modifier_ids: modifierIds });
            }
          });
        });
        const amountInCents = Math.round(orderBreakdown.grandTotal * 100);
        const res = await fetch('/api/create-payment-intent', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            mode: 'weekly',
            meals: mealsForServer,
            order_type: orderType,
            zone_id: matchedZone?.id || null,
            amount: amountInCents,
            metadata: { contact_name: formData.contactName, contact_phone: formData.contactPhone },
          }),
        });
        if (!res.ok) {
          const errBody = await res.json().catch(() => ({}));
          throw new Error(errBody.error || `Payment API error (${res.status})`);
        }
        const { clientSecret, error: apiError } = await res.json();
        if (apiError || !clientSecret) {
          throw new Error(apiError || 'Failed to create payment intent');
        }

        // 2. Confirm card payment with Stripe
        const { error: stripeErr, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: elements.getElement('cardNumber'),
            billing_details: { name: formData.cardHolder },
          },
        });

        if (stripeErr) {
          setStripeError(stripeErr.message);
          setIsProcessing(false);
          return;
        }

        // 3. Payment succeeded — mark to prevent re-submission
        setPaymentSucceeded(true);

        // 4. Verify payment server-side before creating order
        const verifyRes = await fetch('/api/verify-payment', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ payment_intent_id: paymentIntent.id }),
        });
        const verifyData = await verifyRes.json();
        if (!verifyData.verified) {
          throw new Error('Payment verification failed. Please contact support.');
        }

        // 5. Create order (payment verified)
        await handlePayment(formData, orderBreakdown, paymentIntent.id);
      }
    } catch (err) {
      if (paymentSucceeded) {
        // Payment was charged but order creation failed — do NOT allow retry
        setStripeError(t('payment.error_order_failed', '付款已成功，但訂單建立失敗。請聯繫客服處理，勿重新付款。'));
      } else {
        setStripeError(err.message);
        setIsProcessing(false);
      }
    }
  };

  const handleBack = () => {
    if (isProcessing) return;
    navigate('../time');
  };

  // Helper Function renderMealItem (保持不變)
  const renderMealItem = (meal, selection, type) => {
     if (!meal) return null;
     const label = type === 'lunch' ? t('meal_selection.lunch') : t('meal_selection.dinner');
     const tagClass = type === 'lunch' ? 'tag-lunch' : 'tag-dinner';

     return (
       <div className="payment-meal-row">
         {meal.image_url ? (
           <img src={meal.image_url} alt={meal.name} className="payment-meal-thumbnail" loading="lazy" decoding="async" />
         ) : (
           <div className="payment-meal-thumbnail thumbnail-placeholder">
             <ImageIcon size={20} color="#ccc" />
           </div>
         )}
         <div className="payment-meal-info-block">
           <span className={`meal-tag ${tagClass}`}>{label}</span>
           <div className="dish-title-row">
             <span className="payment-meal-name">{meal.name}</span>
             <span className="payment-meal-price">${meal.price.toFixed(2)}</span>
           </div>
           {selection.modifiers && selection.modifiers.length > 0 && (() => {
             const allCurrentMods = modifierGroupsList?.flatMap(g => g.modifiers || []) || [];
             return (
             <div className="mod-breakdown">
               {selection.modifiers.map((mod, idx) => {
                 const current = allCurrentMods.find(m => m.id === mod.id);
                 return (
                 <div key={idx} className="mod-row">
                   <span className="mod-name">+ {current?.name || mod.name}</span>
                   <span className="mod-price">{mod.price > 0 ? `+$${mod.price.toFixed(2)}` : ''}</span>
                 </div>
                 );
               })}
             </div>
             );
           })()}
         </div>
       </div>
     );
  };

  return (
    <>
      <PaymentPageStyles />
      <StepIndicator
        currentStep={bookingMode === 'single' ? 3 : 4}
        bookingMode={bookingMode}
      />

      <div className="payment-page">
        <div className="payment-container">
          <div className="payment-main">

            <form className="payment-form" onSubmit={handleSubmit}>

              {/* 🔥 新增：聯繫與配送資訊區塊 */}
              <h2 className="payment-title">{t('payment.contact_title')}</h2>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{t('payment.contact_name')}</label>
                  <input
                    className="form-input"
                    type="text"
                    name="contactName"
                    placeholder={t('payment.contact_name_placeholder')}
                    value={formData.contactName}
                    onChange={handleInputChange}
                    required
                    disabled={isProcessing}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('payment.contact_phone')}</label>
                  <input
                    className="form-input"
                    type="tel"
                    name="contactPhone"
                    placeholder={t('payment.contact_phone_placeholder')}
                    value={formData.contactPhone}
                    onChange={handleInputChange}
                    required
                    disabled={isProcessing}
                  />
                </div>
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.9rem', color: '#475569', marginBottom: 12 }}>
                <input
                  type="checkbox"
                  checked={saveContact}
                  onChange={toggleSaveContact}
                  style={{ width: 18, height: 18, accentColor: '#2F7D5E', cursor: 'pointer' }}
                />
                {t('payment.save_contact', '記住聯絡資料，下次自動填入')}
              </label>

              {deliveryAddress && (
                <div className="form-group">
                  <label className="form-label">{t('payment.delivery_address', '配送地址')}</label>
                  <div className="readonly-address">
                    {deliveryAddress}{unitNumber ? ` #${unitNumber}` : ''}, {city}, {province} {postalCode}
                  </div>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">{t('payment.delivery_notes')}</label>
                <textarea
                  className="form-textarea"
                  placeholder={t('payment.delivery_notes_placeholder')}
                  value={deliveryNotes || ''}
                  onChange={(e) => setDeliveryNotes(e.target.value)}
                  disabled={isProcessing}
                />
              </div>

              {/* 分隔線 */}
              <hr className="section-divider" />

              {/* 付款資訊區塊 */}
              {isDevMode ? (
                <div style={{ background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 4, padding: '12px 16px', marginBottom: 16, fontSize: '0.85rem', color: '#92400e' }}>
                  DEV MODE — 跳過 Stripe 付款，直接下單
                </div>
              ) : (
                <>
                  <h2 className="payment-title">{t('payment.payment_title')}</h2>

                  <div className="form-group">
                    <label className="form-label">{t('payment.card_holder')}</label>
                    <input
                      className="form-input"
                      type="text"
                      name="cardHolder"
                      placeholder={t('payment.card_holder_placeholder')}
                      value={formData.cardHolder}
                      onChange={handleInputChange}
                      required
                      disabled={isProcessing}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">{t('payment.card_number', '卡號')}</label>
                    <StripeCardInput disabled={isProcessing} />
                    {stripeError && (
                      <div style={{ color: '#ef4444', fontSize: '0.85rem', marginTop: 4 }}>{stripeError}</div>
                    )}
                  </div>
                </>
              )}

              <div className="payment-actions">
                <button type="button" onClick={handleBack} className="payment-btn payment-btn-secondary" disabled={isProcessing}>
                  <ChevronLeft size={18} /> {t('payment.back')}
                </button>

                <button
                  type="submit"
                  className="payment-btn payment-btn-primary"
                  disabled={isProcessing}
                  style={{opacity: isProcessing ? 0.7 : 1}}
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="spinner" size={20} /> {t('payment.processing')}
                    </>
                  ) : isDevMode ? (
                    `測試下單 CAD$ ${orderBreakdown.grandTotal.toFixed(2)}`
                  ) : (
                    <>{t('payment.confirm_pay', { amount: orderBreakdown.grandTotal.toFixed(2) })}</>
                  )}
                </button>
              </div>
            </form>
          </div>

          <div className="payment-summary">
            {/* 訂單摘要內容保持不變 */}
            <h3 className="payment-summary-title">{t('payment.order_summary')}</h3>
            <div className="payment-items">
              {selectedDays.map(dayId => {
                const day = next7Days.find(d => d.id === dayId);
                const daySelections = mealSelections[dayId] || {};
                if (!daySelections.lunch && !daySelections.dinner) return null;

                return (
                  <div key={dayId} className="payment-item">
                    <div className="payment-item-header">
                      <span className="payment-date">{day.fullDate} ({day.weekday})</span>
                    </div>
                    {daySelections.lunch && (() => {
                      const selection = daySelections.lunch;
                      const mealId = typeof selection === 'object' ? selection.id : selection;
                      const meal = menuOptions.find(m => m.id === mealId);
                      return renderMealItem(meal, selection, 'lunch');
                    })()}
                    {daySelections.dinner && (() => {
                      const selection = daySelections.dinner;
                      const mealId = typeof selection === 'object' ? selection.id : selection;
                      const meal = menuOptions.find(m => m.id === mealId);
                      return renderMealItem(meal, selection, 'dinner');
                    })()}
                  </div>
                );
              })}
            </div>

            <div className="payment-total-section">
              <div className="payment-bill-row">
                <span>{t('payment.subtotal')}</span>
                <span>CAD$ {orderBreakdown.subtotal.toFixed(2)}</span>
              </div>
              <div className="payment-bill-row fee-row">
                <span>{t('payment.delivery_fee')}</span>
                <span style={{color: orderBreakdown.deliveryFee === 0 ? '#166534' : 'inherit'}}>
                   {orderBreakdown.deliveryFee === 0 ? t('payment.free') : `CAD$ ${orderBreakdown.deliveryFee.toFixed(2)}`}
                </span>
              </div>
              {orderBreakdown.packagingFee > 0 && (
                <div className="payment-bill-row">
                  <span>{t('payment.packaging_fee', '外賣包裝費')}</span>
                  <span>CAD$ {orderBreakdown.packagingFee.toFixed(2)}</span>
                </div>
              )}
              <div className="payment-bill-row payment-tax">
                <span>GST (5%)</span>
                <span>CAD$ {orderBreakdown.gst.toFixed(2)}</span>
              </div>
              <div className="payment-bill-row payment-tax">
                <span>PST (7%)</span>
                <span>CAD$ {orderBreakdown.pst.toFixed(2)}</span>
              </div>
              <div className="payment-total">
                <span>{t('payment.total')}</span>
                <span>CAD$ {orderBreakdown.grandTotal.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const PaymentPage = () => (
  <Elements stripe={stripePromise}>
    <PaymentPageInner />
  </Elements>
);

export default PaymentPage;
